import { create } from 'zustand'
import { normalizeTagId } from '../utils/tagAllocation'

type TagSkuScanState = {
  rawScans: number
  uniqueTags: string[]
  tagHitCount: Record<string, number>
  skuByTag: Record<string, string>
  ingestTag: (tagId: string) => void
  removeTag: (tagId: string) => void
  setSku: (tagId: string, sku: string) => void
  applySkuToAll: (sku: string, onlyEmpty?: boolean) => void
  clear: () => void
}

/** In-session pool for Engine tag↔SKU mapping — not shared with Birth/Raw Scan. */
export const useTagSkuScanStore = create<TagSkuScanState>((set) => ({
  rawScans: 0,
  uniqueTags: [],
  tagHitCount: {},
  skuByTag: {},

  ingestTag: (tagId) => {
    const t = normalizeTagId(tagId)
    if (!t) return
    set((s) => {
      const tagHitCount = { ...s.tagHitCount, [t]: (s.tagHitCount[t] ?? 0) + 1 }
      const uniqueTags = s.tagHitCount[t] ? s.uniqueTags : [t, ...s.uniqueTags]
      return {
        rawScans: s.rawScans + 1,
        uniqueTags,
        tagHitCount,
      }
    })
  },

  removeTag: (tagId) => {
    const t = normalizeTagId(tagId)
    if (!t) return
    set((s) => {
      if (!s.tagHitCount[t]) return s
      const { [t]: _hit, ...tagHitCount } = s.tagHitCount
      const { [t]: _sku, ...skuByTag } = s.skuByTag
      return {
        uniqueTags: s.uniqueTags.filter((id) => id !== t),
        tagHitCount,
        skuByTag,
      }
    })
  },

  setSku: (tagId, sku) => {
    const t = normalizeTagId(tagId)
    if (!t) return
    set((s) => ({
      skuByTag: { ...s.skuByTag, [t]: sku },
    }))
  },

  applySkuToAll: (sku, onlyEmpty = false) => {
    const value = sku.trim()
    if (!value) return
    set((s) => {
      const skuByTag = { ...s.skuByTag }
      for (const tagId of s.uniqueTags) {
        if (onlyEmpty && skuByTag[tagId]?.trim()) continue
        skuByTag[tagId] = value
      }
      return { skuByTag }
    })
  },

  clear: () => set({ rawScans: 0, uniqueTags: [], tagHitCount: {}, skuByTag: {} }),
}))
